/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useMemo, useRef } from "react";
import styled from "styled-components";
import TimeSlotItem, { StyledTimeSlotItem } from "./TimeSlotItem";
import TimeSlotsSkeleton from "./skeleton/TimeSlotsSkeleton";
import T from "./ui/Translation";
import { useClickableA11y } from "../hooks/a11y/useClickableA11y";

const StyledTimeSlots = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem 0.5rem;

  ${({ theme }) => theme.containerMedia.sm} {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MoreSlotsButton = styled(StyledTimeSlotItem)`
  color: rgba(${({ theme }) => theme.colors.accent.rgba});

  @media (hover: hover) {
    &:hover {
      color: rgba(${({ theme }) => theme.colors.accentContrast.rgba});
    }
  }
`;

const TimeSlots = ({
  slots,
  isLoading,
  selectedTime,
  pendingTime,
  onSlotClick,
  disabled,
  maxSlots,
  onMoreSlotsClick,
}) => {
  const slotsToShow = useMemo(() => (maxSlots ? slots?.slice(0, maxSlots) : slots), [slots, maxSlots]);
  const hasMoreSlots = useMemo(() => maxSlots && slots && slots.length > maxSlots, [slots, maxSlots]);
  const moreSlotsA11yProps = useClickableA11y({ onClick: onMoreSlotsClick });
  const showsMoreButton = !!(hasMoreSlots && onMoreSlotsClick);

  const lastCellCount = useRef(0);
  if (!isLoading && slotsToShow) {
    lastCellCount.current = slotsToShow.length + (showsMoreButton ? 1 : 0);
  }

  return (
    <StyledTimeSlots>
      {isLoading && <TimeSlotsSkeleton count={lastCellCount.current || undefined} />}
      {!isLoading &&
        slotsToShow?.map((slot) => (
          <TimeSlotItem
            key={slot.id}
            slot={slot}
            isSelected={selectedTime === slot.time}
            isPending={pendingTime === slot.time}
            disabled={disabled}
            onClick={onSlotClick}
          />
        ))}
      {!isLoading && showsMoreButton && (
        <MoreSlotsButton onClick={onMoreSlotsClick} {...moreSlotsA11yProps}><T>More slots</T></MoreSlotsButton>
      )}
    </StyledTimeSlots>
  );
};

export default TimeSlots;
